/**
 * Пуш-уведомления на этом телефоне (PWA) — docs/PWA.md.
 *
 * Подписка — устройства: колокольчик в телефонной шапке включает и
 * выключает её. На iPhone пуши есть только у приложения, добавленного на
 * экран «Домой» (iOS 16.4+), — в Safari-вкладке их нет совсем.
 */

import type { ApiClient } from './api.ts';
import { isDesktop } from './desktop.ts';
import { isIos, isStandalone } from './pwa.ts';

export type PushState = 'unsupported' | 'needs-install' | 'denied' | 'off' | 'on';

export const PUSH_LABELS: Record<PushState, string> = {
  unsupported: 'Уведомления на этом устройстве недоступны',
  'needs-install': 'Уведомления — после установки на экран «Домой»',
  denied: 'Уведомления запрещены в настройках телефона',
  off: 'Включить уведомления на этом телефоне',
  on: 'Уведомления на этом телефоне включены',
};

function supported(): boolean {
  return !isDesktop() && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

export async function pushState(): Promise<PushState> {
  if (isIos() && !isStandalone()) return 'needs-install';
  if (!supported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  return sub && Notification.permission === 'granted' ? 'on' : 'off';
}

/** Ключ VAPID из base64url — в байты для pushManager.subscribe. */
function keyBytes(base64url: string): Uint8Array {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/**
 * Включить: разрешение (только по нажатию — иначе iPhone откажет), подписка
 * у браузера, регистрация на сервере. Возвращает итоговое состояние.
 */
export async function enablePush(api: ApiClient): Promise<PushState> {
  const state = await pushState();
  if (state !== 'off' && state !== 'on') return state;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';
  const { publicKey } = await api.pushKey();
  if (!publicKey) throw new Error('Пуш-уведомления на сервере ещё не настроены');
  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) as BufferSource }));
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api.pushSubscribe({ endpoint: json.endpoint, keys: json.keys });
  return 'on';
}

/** Выключить на этом телефоне (и при выходе из приложения). Ошибки глушатся. */
export async function disablePush(api: ApiClient): Promise<void> {
  try {
    const reg = await registration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    await api.pushUnsubscribe(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe();
  } catch {
    // выход не должен застревать на пушах
  }
}
